import React, { useState, useEffect} from 'react';
import { useHistory, useNavigate } from "react-router-dom";
import axios from "axios";
import { Link, NavLink } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import DepartmentProjectsChart from './DepartmentProjectsChart'; // Import the pie chart component
import DepartmentProjectBarChart from './DepartmentProjectBarChart';
import CountUp from 'react-countup';
import { Card, Row, Col, Table, Spinner, Form, Button } from "react-bootstrap";
// Register the scales and elements
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDeptId, setSelectedDeptId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 20;
    const [totalPages, setTotalPages] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [keywordQuery, setKeywordQuery] = useState('');
    const [selectedProjects, setSelectedProjects] = useState(new Set());
    const [totalProjectCount, setTotalProjectCount] = useState(0);
    const [projectTypeCounts, setProjectTypeCounts] = useState({});
    const [unfilteredTotalProjectCount, setUnfilteredTotalProjectCount] = useState(0);
    const [unfilteredProjectTypeCounts, setUnfilteredProjectTypeCounts] = useState({});
    const [barChartData, setBarChartData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [totalCount, setTotalCount] = React.useState(0);  // Initialize total count
    

    const navigate = useHistory();
    const history = useHistory();

    // Fetch departments
    useEffect(() => {
        const getDepartments = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
                setDepartments(response.data);
            } catch (error) {
                console.error("Error fetching departments:", error);
                setError("Failed to fetch departments.");
            }
        };
        getDepartments();
    }, []);


    const handleRenew = (mouId,title) => {
        localStorage.setItem("projId", mouId+' - '+title); // Store MOU ID in localStorage
        window.location.href = "/projectuc"; // Navigate to mou_renewal
       };

    // Fetch projects for bar chart
    // Function to fetch project counts based on date range
    
    const fetchProjectCounts = async () => {
        setLoading(true);
        try {
            // Prepare query parameters based on start and end dates
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            // Send request to the backend with optional date filters
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/countByType`, { params });
            const projectsByType = response.data || {};

            // Update the project type counts state
            setProjectTypeCounts(projectsByType);

            // Prepare bar chart data based on response
            const chartData = {
                labels: Object.keys(projectsByType),
                datasets: [
                    {
                        label: 'Number of Projects per Type',
                        data: Object.values(projectsByType),
                        backgroundColor: [
                            'rgba(235, 90, 6, 0.57)',
                            'rgba(54, 162, 235, 0.6)',
                            'rgba(255, 206, 86, 0.6)',
                            'rgba(192, 75, 167, 0.93)',
                            'rgba(133, 255, 102, 0.6)',
                            'rgba(131, 64, 255, 0.6)',
                        ],
                        borderColor: 'rgba(0, 0, 0, 1)',
                        borderWidth: 1,
                    },
                ],
            };
            setBarChartData(chartData);
        } catch (error) {
            console.error('Error fetching project counts:', error);
        } finally {
            setLoading(false);
        }
    };
    
     // Fetch initial overall project counts on component mount
     useEffect(() => {
        fetchProjectCounts();
    }, []);

    // Refetch project counts when startDate or endDate changes
    useEffect(() => {
        fetchProjectCounts();
    }, [startDate, endDate]);
    // Call this function when needed, for example, in useEffect or on button click

    useEffect(() => {
        const fetchUnfilteredProjectCounts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/count`);
                const data = response.data || {};
    
                setUnfilteredTotalProjectCount(data.totalProjects);
                setUnfilteredProjectTypeCounts(data.projectsByType || {});
            } catch (error) {
                console.error('Error fetching unfiltered project counts:', error);
            }
        };
    
        fetchUnfilteredProjectCounts();
    }, []);

    useEffect(() => {
        const fetchTotalElements = async () => {
            try {
                // Fetch initial response to get total elements
                const initialUrl = `${process.env.REACT_APP_API_BASE_URL}/api/projects?deptId=${selectedDeptId}&searchQuery=${searchQuery}`;
                const initialResponse = await axios.get(initialUrl);

                if (!initialResponse.data || !initialResponse.data.page || !initialResponse.data.page.totalElements) {
                    throw new Error("Invalid response format");
                }

                // Update total elements count immediately
                const totalElements = initialResponse.data.page.totalElements;
                setTotalProjectCount(totalElements);
                setTotalPages(Math.ceil(totalElements / 10)); // Assuming 10 projects per page
                
                // Fetch full project data after getting the total count
                fetchFullProjectData(totalElements);
            } catch (error) {
                console.error("Error fetching total elements:", error);
                setError("Failed to fetch total elements.");
                setLoading(false);
            }
        };

        const fetchFullProjectData = async (totalElements) => {
            try {
                const fullDataUrl = `${process.env.REACT_APP_API_BASE_URL}/api/projects?size=${totalElements}&deptId=${selectedDeptId}&searchQuery=${searchQuery}`;
                const response = await axios.get(fullDataUrl);

                // Extract projects from response
                const allProjects = response.data._embedded ? response.data._embedded.projects : [];

                // Update state
                setProjects(allProjects);
                
                // Calculate project type counts
                const typeCounts = allProjects.reduce((acc, project) => {
                    const type = project.projType;
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                }, {});
                setProjectTypeCounts(typeCounts);
            } catch (error) {
                console.error("Error fetching full project data:", error);
                setError("Failed to fetch project data.");
            } finally {
                setLoading(false);
            }
        };

        fetchTotalElements();
    }, [searchQuery, selectedDeptId]);
    
    const handleDeleteSelected = async () => {
        try {
            const deletePromises = Array.from(selectedProjects).map(projId => {
                return axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projId}`);
            });
            await Promise.all(deletePromises);
            setSelectedProjects(new Set());
            // Re-fetch the updated project list after deletion
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects?page=${currentPage}&size=${pageSize}&deptId=${selectedDeptId}`);
            const updatedProjects = response.data._embedded ? response.data._embedded.projects : [];
            setProjects(updatedProjects);
        } catch (error) {
            console.error("Error deleting projects:", error);
            setError("Failed to delete selected projects. Please try again.");
        }
    };

    const handleCheckboxChange = (projId) => {
        const newSelectedProjects = new Set(selectedProjects);
        if (newSelectedProjects.has(projId)) {
            newSelectedProjects.delete(projId);
        } else {
            newSelectedProjects.add(projId);
        }
        setSelectedProjects(newSelectedProjects);
    };

    const handleDeptChange = (e) => {
        setSelectedDeptId(e.target.value);
        setCurrentPage(0); // Reset to first page when department changes
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    if (loading) {
        return (
            <div className="container" style={{
                position: 'absolute',  // Keep it absolute for positioning
                top: '40%',            // Position it 40% from the top of the page
                left: '50%',           // Horizontally center the container
                transform: 'translateX(-50%)', // Offset by 50% of its width to truly center
                display: 'flex',       // Flexbox for centering
                justifyContent: 'center', // Center content horizontally
                alignItems: 'center',  // Align content vertically
                zIndex: 1,             // Ensure it's on top of other content
                width: '100%',         // Full width of the page
                backgroundColor: 'transparent',  // Transparent background
                paddingTop: '20px',    // Optional padding from the top
            }}>
                {/* Loader and text */}
                <div style={{
                    textAlign: 'center',
                    zIndex: 2,
                    animation: 'fadeInUp 1.5s ease-in-out'
                }}>
                    <Spinner
                        animation="border"
                        variant="primary"
                        style={{
                            width: '50px',
                            height: '50px',
                            borderWidth: '5px',
                            animation: 'spin 2s linear infinite'
                        }}
                    />
                    <p style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#5a5a5a',
                        marginTop: '20px',
                        animation: 'fadeInText 2s ease-out forwards'
                    }}>
                        Please Wait...
                    </p>
                </div>
    
                {/* Keyframes for spinner rotation, text fade-in, and up effect */}
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes fadeInText {
                        0% { opacity: 0; transform: translateY(10px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fadeInUp {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }
    

    const filteredProjects = projects.filter(obj => {
        const lowerCaseQuery = searchQuery.toLowerCase();
        const lowerCaseKeyword = keywordQuery.toLowerCase();
    
        // Check if searchQuery (for title) is provided and matches any fields
        const titleSearchMatches = lowerCaseQuery
            ? (
                (obj.projTitle?.toLowerCase() || '').includes(lowerCaseQuery) ||
                (obj.projType?.toLowerCase() || '').includes(lowerCaseQuery) ||
                (obj.projContributors?.toLowerCase() || '').includes(lowerCaseQuery) ||
                (obj.projDept?.toLowerCase() || '').includes(lowerCaseQuery) ||
                (obj.trlLevel?.toString() || '').includes(lowerCaseQuery) ||
                (obj.projId?.toString() || '').includes(lowerCaseQuery)
            )
            : true; // If no title search, default to true
    
        // Check if keywordQuery (for keywords) is provided and matches projKeyword field
        const keywordSearchMatches = lowerCaseKeyword
            ? (obj.projKeyword?.toLowerCase() || '').includes(lowerCaseKeyword)
            : true; // If no keyword search, default to true
    
        // Return true if either title search or keyword search matches independently
        return titleSearchMatches && keywordSearchMatches;
    });

    
const fetchTotalProjectCount = async () => {
    try {
        // Step 1: Fetch all departments
        const departmentResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
        const allDepartments = departmentResponse.data || [];
        
        // Filter out departments with empty, null or undefined deptId
        const departments = allDepartments.filter(dept => dept && dept.deptId && dept.deptId.trim() !== "");
  
        // Step 2: Extract department IDs
        const deptIds = departments.map(dept => dept.deptId);
        console.log(deptIds)
        // Step 3: Fetch project counts for each department
        const responses = await Promise.all(
            deptIds.map(deptId =>
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/project-departments/project-type-count/${deptId}`)
            )
        );
  
        // Step 4: Calculate total project count by summing all values for each department
        let totalCount = 0;
        responses.forEach((response, index) => {
            console.log(`Response for ${departments[index].deptName}:`, response.data);
  
            // Sum all counts in the response object
            const departmentCounts = response.data;
            for (const key in departmentCounts) {
                if (departmentCounts.hasOwnProperty(key)) {
                    const count = departmentCounts[key];
                    totalCount += typeof count === 'number' ? count : 0;  // Add only if the value is a number
                }
            }
        });
        setTotalCount(totalCount); 
        console.log("Total Project Count:", totalCount);
        return totalCount;  // Return the total project count
    } catch (error) {
        console.error("Failed to fetch total project count:", error);
        throw new Error("Failed to fetch total project count.");
    }
  };
  
  fetchTotalProjectCount()
  .then(totalCount => console.log("Total Project Count:1", totalCount))
  .catch(error => console.error(error.message));
  

    return (
        <div className="" style={{paddingLeft: '20px',  // Adjust as needed
            paddingRight: '20px'  // Adjust as needed
        }}>
            {/* Total Project Count and Project Type Counts */}
            <div className="container d-flex justify-content-end mt-0">

            <Link 
    to="/projfilter" 
    className="p-2 text-center border rounded text-light text-decoration-none me-2" // Removed underline
    style={{
      cursor: 'pointer',
      width: '150px', // Width remains as desired
      height: '35px', // Explicitly set height
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)', 
      transition: 'background-color 0.3s, color 0.3s', 
      display: 'flex', // To vertically center text
      justifyContent: 'center', // Center horizontally
      alignItems: 'center', // Center vertically
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085'; 
      e.target.style.color = 'white'; 
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)'; 
      e.target.style.color = 'white'; 
    }}
  >
     Projects Filter
  </Link>

  <Link 
    to="/addproject" 
    className="p-2 text-center border rounded text-light text-decoration-none me-2" // Removed underline
    style={{
      cursor: 'pointer',
      width: '150px', // Width remains as desired
      height: '35px', // Explicitly set height
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)', 
      transition: 'background-color 0.3s, color 0.3s', 
      display: 'flex', // To vertically center text
      justifyContent: 'center', // Center horizontally
      alignItems: 'center', // Center vertically
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085'; 
      e.target.style.color = 'white'; 
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)'; 
      e.target.style.color = 'white'; 
    }}
  >
    Add Project
  </Link>


  <Link 
    to="/projrev" 
    className="p-2 text-center border rounded text-light text-decoration-none me-2" // Removed underline
    style={{
      cursor: 'pointer',
      width: '150px', // Width remains as desired
      height: '35px', // Explicitly set height
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)', 
      transition: 'background-color 0.3s, color 0.3s', 
      display: 'flex', // To vertically center text
      justifyContent: 'center', // Center horizontally
      alignItems: 'center', // Center vertically
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085'; 
      e.target.style.color = 'white'; 
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)'; 
      e.target.style.color = 'white'; 
    }}
  >
    ProjectRevenue
  </Link>
  
  
  
  {["1", "2", "3"].includes(sessionStorage.getItem("access_level")) ? (
  <Link 
    to="/dstatus" 
    className="p-2 text-center border rounded text-light text-decoration-none"
    style={{
      cursor: 'pointer',
      width: '200px',
      height: '35px',
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)',
      transition: 'background-color 0.3s, color 0.3s',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085';
      e.target.style.color = 'white';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)';
      e.target.style.color = 'white';
    }}
  >
    Approval Level 2
  </Link>
) : sessionStorage.getItem("access_level") === "4" ? (
  <Link 
    to="/hodstatus" 
    className="p-2 text-center border rounded text-light text-decoration-none"
    style={{
      cursor: 'pointer',
      width: '200px',
      height: '35px',
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)',
      transition: 'background-color 0.3s, color 0.3s',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085';
      e.target.style.color = 'white';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)';
      e.target.style.color = 'white';
    }}
  >
    Approval Level 2
  </Link>
) : sessionStorage.getItem("access_level") === "5" ? (
  <Link 
    to="/projstatus" 
    className="p-2 text-center border rounded text-light text-decoration-none"
    style={{
      cursor: 'pointer',
      width: '200px',
      height: '35px',
      textAlign: 'center',
      backgroundColor: 'var(--bs-primary)',
      transition: 'background-color 0.3s, color 0.3s',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#004085';
      e.target.style.color = 'white';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'var(--bs-primary)';
      e.target.style.color = 'white';
    }}
  >
    Approval Level 1
  </Link>
) : null}


</div>
            <div className="row mb-4">
                <div className="col-md-3 mt-4">
                    <div className="total-count-box bg-light p-3 border rounded">
                        <h4>Total Projects</h4>
                        <p className="display-5" style={{ fontWeight: 'bold', fontSize: '3rem', color: '' }}>
            <CountUp 
                start={0} 
                end={totalProjectCount} 
                duration={2}  // Duration of the animation in seconds
                separator=","
                decimals={0} // Remove decimals if you want a whole number
                prefix="" // Optional: If you want to add a prefix
                suffix="" // Optional: If you want to add a suffix
            />
        </p>
                    </div>
                </div>
                {/* Project Type Counts */}
                {/* Project Type Counts */}
                <div className="col-md-9 mt-4">
                <div className="row">
            {/* Render counts for each project type */}
            {Object.keys(unfilteredProjectTypeCounts).map((type, index) => (
                <div className="col-md-4 mb-3" key={index}>
                    <div
  className="stat-box text-white p-3 rounded"
  style={{
    backgroundColor:
      index % 6 === 0
        ? 'rgba(13, 61, 150, 0.6)' // Blue
        : index % 6 === 1
        ? 'rgba(186, 9, 147, 0.6)' // Pink
        : index % 6 === 2
        ? 'rgba(75, 192, 192, 0.6)' // Cyan
        : index % 6 === 3
        ? 'rgba(153, 102, 255, 0.6)' // Purple
        : index % 6 === 4
        ? 'rgba(255, 159, 64, 0.6)' // Orange
        : 'rgba(255, 99, 132, 0.6)' // Red
  }}
>  

                        <p>{type}</p>
                        <h5>{unfilteredProjectTypeCounts[type] || 0}</h5>
                    </div>
                </div>
            ))}
        </div>
    </div>
            
            
           

            {/* Charts */}
            <div className="row">
    {/* Left Side: Pie Chart and Bar Chart stacked */}
    <div className="col-md-4 d-flex flex-column">
        {/* Pie Chart Component */}
        <div className="mb-3">
            <DepartmentProjectsChart plugins={{ datalabels: { color: 'white', font: { weight: 'bold', size: 14 } } }} />
        </div>

        {/* Bar Chart Component */}
       {/*} <div>
            <div className="d-flex align-items-center mb-3">
                <div className="me-2">
                    <label htmlFor="startDate">Start Date:</label>
                    <input
                        type="date"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="me-2">
                    <label htmlFor="endDate">End Date:</label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <button className="btn btn-primary me-2" onClick={fetchProjectCounts}>
                    Filter
                </button>
                <button
                    className="btn btn-secondary"
                    onClick={() => {
                        setStartDate(''); // Clear start date
                        setEndDate('');   // Clear end date
                        fetchProjectCounts(); // Fetch overall count with empty dates
                    }}
                >
                    Any
                </button>
            </div>

            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                {Object.keys(projectTypeCounts).length === 0 ? (
                    <p>No chart data available</p>
                ) : (
                    <Bar data={barChartData} />
                )}
            </div>
        </div>*/}
    </div>

    {/* Right Side: New Chart Fully Occupying Right Space */}
    <div className="col-md-8">
        <DepartmentProjectBarChart startDate={startDate} endDate={endDate} />
    </div>
</div>
          
           

            {/* Filter and Search */}
            <div className="container">
                <div className="row mb-4">
                    {/*<div className="col-md-4">
                        <label htmlFor="department" className="form-label">Filter by Department</label>
                        <select id="department" className="form-select" value={selectedDeptId} onChange={handleDeptChange}>
                            <option value="">All Departments</option>
                            {departments.map(dept => (
                                <option key={dept.deptId} value={dept.deptId}>
                                    {dept.deptName}
                                </option>
                            ))}
                        </select>
                    </div>*/}
                    <div className="col-md-4">
                        <label htmlFor="search" className="form-label">Search Project By Title:</label>
                        <input
                            type="text"
                            id="search"
                            className="form-control"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="col-md-4">
                    <label htmlFor="keyword" className="form-label">Search by Keyword</label>
                    <input
                        type="text"
                        id="keyword"
                        className="form-control"
                        value={keywordQuery}
                        onChange={(e) => setKeywordQuery(e.target.value)} // Update state on change
                    />
                </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

<div className="row">
    <div className="col-12">
        {filteredProjects.length === 0 ? (
            <div className="alert alert-primary text-center">No Record Found.....</div>
        ) : (
            <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
            <table className="table table-striped table-bordered table-hover rounded-5">
      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                        <tr>
                            <th scope="col" className='bg-secondary'>
                                <input
                                    type="checkbox"
                                    onChange={() => {
                                        if (selectedProjects.size === filteredProjects.length) {
                                            setSelectedProjects(new Set());
                                        } else {
                                            setSelectedProjects(new Set(filteredProjects.map(proj => proj.projId)));
                                        }
                                    }}
                                    checked={selectedProjects.size === filteredProjects.length}
                                />
                            </th>
                            <th scope="col" className='bg-secondary'>Project ID</th>
                            <th scope="col" className='bg-secondary'>Project Type</th>
                            <th scope="col" className='bg-secondary'>Title</th>
                            {/*<th scope="col" className='bg-secondary'>TRL Level</th>*/}
                            <th scope="col" className='bg-secondary'>Keyword</th>
                            <th scope="col" className='bg-secondary'>Action</th>
                            <th scope="col" className='bg-secondary'>UC Document</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProjects.map((obj, index) => (
                            <tr key={index}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedProjects.has(obj.projId)}
                                        onChange={() => handleCheckboxChange(obj.projId)}
                                    />
                                </td>
                                <th scope="row">{obj.projId}</th>
                                <td>{obj.projType}</td>
                                <td>{obj.projTitle}</td>
                                {/*<td>{obj.trlLevel}</td>*/}
                                <td>{obj.projKeyword}</td>
                                <td>
                                <button 
                                        className="btn btn-primary" 
                                        onClick={() => navigate.push(`/viewproject/${obj.projId}`)}
                                    >
                                        View Details
                                    </button>
                                    </td>
                                    <td>
                                    <a
            href="#"
            style={{ cursor: "pointer" }}
            onClick={() => handleRenew(obj.projId, obj.projTitle)}
          >
            View UcDocument
          </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
</div>


 {/* "Delete Selected" button */}
 {selectedProjects.size > 0 && (
                <div className="mt-3 text-end">
                    <button
                        className="btn btn-danger"
                        onClick={handleDeleteSelected}
                    >
                        Delete Selected
                    </button>
                </div>
            )}

 
                
            </div>
        </div>
        </div>
    );
};

export default Projects;
